import React from 'react';
import { MUIStyledCommonProps, styled } from '@mui/system';

import { ContentModuleRenderer, getBorderRadius, MobileLayoutTheme } from '../renderContentModule';
import { ILoyaltyBalanceModule } from '../models/ContentModule';
import { ContentAnchorProperties } from '../models/ContentTemplate';
import { Column } from 'ui/Flex';
import { Typography } from '@mui/material';

interface StyledLoyaltyBalanceProps {
    visualisation: ILoyaltyBalanceModule['properties']['visualisation'];
    layoutTheme: MobileLayoutTheme;
    properties: ContentAnchorProperties;
}

interface LoyaltyBalanceModuleProps {
    loyaltyBalance: ILoyaltyBalanceModule;
    theme: MobileLayoutTheme;
    properties: ContentAnchorProperties;
}

const getVerticalStyle = (alignment: string) => {
    switch (alignment) {
        case 'TOP':
            return 'start';
        case 'MIDDLE':
            return 'center';
        case 'BOTTOM':
            return 'end';
        default:
            return 'start';
    }
};

const getTextAlignmentStyle = (alignment: string) => {
    switch (alignment) {
        case 'LEFT':
            return 'left';
        case 'CENTER':
            return 'center';
        case 'RIGHT':
            return 'end';
        default:
            return 'left';
    }
};

const PREFIX = 'renderLoyaltyBalanceModule';

const classes = {
    root: `${PREFIX}-root`,
    title: `${PREFIX}-title`,
    content: `${PREFIX}-content`,
    image: `${PREFIX}-image`,
    backgroundOverlayColour: `${PREFIX}-backgroundOverlayColour`,
    textContainer: `${PREFIX}-textContainer`,
    subtitleText: `${PREFIX}-subtitleText`,
    subtitleTwoText: `${PREFIX}-subtitleTwoText`,
    errorMessage: `${PREFIX}-errorMessage`
};

const StyledColumn = styled(Column)(
    ({
        theme,
        layoutTheme,
        properties,
        visualisation
    }: StyledLoyaltyBalanceProps & MUIStyledCommonProps) => ({
        '&:hover': {
            // eslint-disable-next-line no-nested-ternary
            backgroundColor: properties.isSelectable
                ? properties.isSelected
                    ? 'rgba(92, 92, 92, 0.16)'
                    : 'rgba(92, 92, 92, 0.12)'
                : 'initial',
            transform: `${properties.isSelectable && !properties.isSelected ? 'scale(1.025)' : 'none'}`
        },
        width: '100%',
        padding: theme.spacing(0.5),
        paddingTop: '0px',
        margin: '0px',
        marginTop: theme.spacing(0.5),
        backgroundColor: properties.isSelected ? 'rgba(92, 92, 92, 0.16)' : 'initial',
        cursor: properties.isSelectable ? 'pointer' : 'inherit',
        transition: 'background-color 0.1s ease-in, transform 0.1s ease-in',
        border: `2px solid ${
            properties.isSelectable && properties.isSelected ? 'rgb(62, 141, 208)' : 'rgba(0,0,0,0)'
        }`,
        [`& .${classes.root}`]: {
            width: '100%',
            height: theme.spacing(1 + (layoutTheme.height * 2) / 8),
            padding: theme.spacing(0.5),
            position: 'relative',
            borderRadius: getBorderRadius(properties),
            transition: 'border-radius .15s linear',
            overflow: 'hidden'
        },
        [`& .${classes.backgroundOverlayColour}`]: {
            position: 'absolute',
            top: 0,
            width: '100%',
            height: '100%',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            backgroundColor: visualisation?.backgroundOverlayColour
        },
        [`& .${classes.title}`]: {
            transition: 'color .15s linear',
            color: properties.titleTextColour ? properties.titleTextColour : properties.anchorTextColour,
            lineHeight: '1rem',
            fontSize: layoutTheme.fontSize
        },
        [`& .${classes.image}`]: {
            position: 'absolute',
            top: 0,
            width: '100%',
            height: '100%',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0
        },
        [`& .${classes.textContainer}`]: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: theme.spacing(1),
            zIndex: 20,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: getVerticalStyle(visualisation?.verticalAlignment)
        },
        [`& .${classes.subtitleText}`]: {
            fontSize: layoutTheme.fontSize,
            fontWeight: 'bold',
            color: visualisation?.textColour,
            lineHeight: '1rem'
        },
        [`& .${classes.subtitleTwoText}`]: {
            fontSize: layoutTheme.fontSize - 1,
            fontWeight: 'bold',
            color: visualisation?.textColour,
            lineHeight: '1rem'
        },
        [`& .${classes.errorMessage}`]: {
            textAlign: 'center',
            verticalAlign: 'middle',
            lineHeight: '26px',
            paddingTop: '10%'
        }
    })
);

export const renderLoyaltyBalanceModule: ContentModuleRenderer = (
    loyaltyBalance: ILoyaltyBalanceModule,
    theme: MobileLayoutTheme,
    properties: ContentAnchorProperties,
    _
) => <LoyaltyBalanceModuleComponent loyaltyBalance={loyaltyBalance} theme={theme} properties={properties} />;

function LoyaltyBalanceModuleComponent({ loyaltyBalance, theme, properties }: LoyaltyBalanceModuleProps) {
    const { visualisation } = loyaltyBalance?.properties || {};
    const timeString = React.useMemo(() => new Date().toTimeString(), []);

    const isImageAvailable = React.useMemo(
        () => !!visualisation?.backgroundImageUrl,
        [visualisation?.backgroundImageUrl]
    );

    const handleClick = React.useCallback(() => {
        if (properties.handleClick && !properties.isSelected) {
            properties.handleClick();
        }
    }, [properties]);

    return (
        <StyledColumn visualisation={visualisation} layoutTheme={theme} properties={properties}>
            <Typography className={classes.title}>{loyaltyBalance.title}</Typography>
            <div className={classes.root} onClick={handleClick}>
                {isImageAvailable && (
                    <img
                        className={classes.image}
                        src={`${visualisation?.backgroundImageUrl}?t=${timeString}`}
                        alt=""
                    />
                )}
                <div
                    className={classes.backgroundOverlayColour}
                    style={{
                        backgroundColor: visualisation?.backgroundOverlayColour
                    }}
                />
                <div className={classes.textContainer}>
                    <Typography
                        className={classes.subtitleText}
                        textAlign={getTextAlignmentStyle(visualisation?.textAlignment)}
                    >
                        {visualisation?.subtitleText}
                    </Typography>
                    <Typography
                        className={classes.subtitleTwoText}
                        textAlign={getTextAlignmentStyle(visualisation?.textAlignment)}
                    >
                        {visualisation?.subtitleTwoText}
                    </Typography>
                </div>
            </div>
        </StyledColumn>
    );
}
